import { readRefreshToken, authStorageKey } from "../axios_Interceptor/authSession";
import { StatePage } from "../UI/ContentState";
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../axios_Interceptor/api";
import url from "../server";
import { SellerAdminPermissionKey } from "../adminPermissions";

interface AdminRouteProps {
  component: React.ComponentType<any>;
  component_m?: React.ComponentType<any>;
  allowedLevels?: string[];
  requiredPermissions?: SellerAdminPermissionKey[];
  permissionMode?: "any" | "all";
}

const AdminRoute: React.FC<AdminRouteProps> = ({
  component: Component,
  component_m: Component_Mobile,
  allowedLevels,
  requiredPermissions,
  permissionMode = "any",
}) => {
  const ismobile = /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const [level, setLevel] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    // New tabs opened with noopener have no sessionStorage. Let the API restore
    // the shared cookie session and verify the account before deciding access.
    api.get(url.Communication_url.user_info, { handleAuthErrorLocally: true, authScope: "seller" })
      .then(response => { if (active) {
        const userLevel = response.data?.user_level || "";
        if (!["master", "cs", "linkcam"].includes(userLevel)) { setError(true); return; }
        readRefreshToken("seller");
        const storage = localStorage.getItem("seller_key") ? localStorage : sessionStorage;
        storage.setItem(authStorageKey("authority", "seller"), "ok");
        setLevel(userLevel);
        setPermissions(response.data?.admin_permissions || []);
      } })
      .catch(cause => { if (active) {
        if (cause?.code === "AUTH_REQUIRED" || [401, 403].includes(cause?.response?.status)) setError(true);
        else setLoadError(true);
      } });
    return () => { active = false; };
  }, [attempt]);

  if (error) return <Navigate to="/admin/login" replace />;
  if (loadError) return <StatePage label="셀러 관리" backHref="/admin/login" kind="error" icon="account" title="계정 정보를 불러오지 못했습니다." description="연결 상태를 확인한 뒤 다시 시도해 주세요." action={{ label: "다시 시도", onClick: () => setAttempt(value => value + 1) }} secondaryAction={{ label: "로그인 화면", href: "/admin/login" }} />;
  if (level === null) return <StatePage label="셀러 관리" backHref="/admin/login" kind="loading" icon="account" title="계정 정보를 확인하고 있습니다." description="잠시만 기다려 주세요." />;
  if (allowedLevels && !allowedLevels.includes(level)) {
    return <Navigate to={level === "cs" ? "/admin/customer-board" : level === "linkcam" ? "/admin/payment-cancel" : "/admin/dashboard"} replace />;
  }
  const hasRequiredPermissions = !requiredPermissions?.length || (permissionMode === "all"
    ? requiredPermissions.every(permission => permissions.includes(permission))
    : requiredPermissions.some(permission => permissions.includes(permission)));
  if (level !== "master" && !hasRequiredPermissions) {
    const fallback = permissions.includes("menu_inquiry") ? "/admin/customer-board"
      : permissions.includes("menu_live") ? "/admin/livelist"
      : permissions.includes("menu_orders") ? "/admin/order-list"
      : permissions.includes("menu_members") ? "/admin/userlist/user"
      : permissions.includes("menu_coin") ? "/admin/coinmanage"
      : permissions.includes("menu_settings") ? "/admin/setting"
      : permissions.includes("account_manage") || permissions.includes("account_permission") ? "/admin/staff"
      : "/admin/login";
    return <Navigate to={fallback} replace />;
  }
  const MobileComponent = Component_Mobile;
  if (ismobile && MobileComponent) return <MobileComponent adminPermissions={permissions} adminLevel={level} />;
  return <Component adminPermissions={permissions} adminLevel={level} />;
};

export default AdminRoute;
